import { thumb } from "../lib/site";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SEO from "../components/SEO";

import { Reveal, Stagger, Item, Chapter, MaskedLine } from "../components/Reveal";
import { openLeadPopup } from "../components/Navbar";
import { PROJECTS } from "../data/content";
import { track } from "../lib/analytics";

export default function Projects() {
  return (
    <main className="pt-36 lg:pt-44">
      <SEO
        title="Recent Work — Website Projects & Case Studies | Grotech Web"
        description="Website projects built around lead generation: B2B manufacturing, real estate and consulting. See the strategy behind each build."
        path="/projects"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Projects", path: "/projects" }]}
      />
      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16" data-testid="projects-hero">
        <Chapter index="—" label="Portfolio" />
        <h1 className="mt-8 max-w-4xl font-display text-4xl font-black leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">
          <MaskedLine>Recent Work.</MaskedLine>
          <MaskedLine delay={0.15}>
            <span className="text-white/35">Built to Generate Business.</span>
          </MaskedLine>
        </h1>
        <Reveal hero delay={0.3}>
          <p className="mt-6 max-w-2xl text-white/60">
            Real websites, live and working for real businesses. Each one is built around the same
            principle: get found on Google, build trust fast, turn visitors into enquiries.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-16 lg:py-24">
        <Stagger className="grid gap-8 lg:grid-cols-3">
          {PROJECTS.map((p, i) => (
            <Item key={p.slug} className={i === 0 ? "lg:col-span-2" : ""}>
              <Link
                to={`/projects/${p.slug}/`}
                data-testid={`projects-card-${p.slug}`}
                className="group block h-full overflow-hidden rounded-3xl border border-white/10 bg-ink"
              >
                <div className={`relative overflow-hidden ${i === 0 ? "aspect-[16/9]" : "aspect-[4/3]"}`}>
                  <img
                    src={thumb(p.image)}
                    alt={`${p.name} — ${p.industry} website`}
                    loading="lazy"
                    className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                  {p.sample ? (
                    <span className="absolute left-4 top-4 rounded-full bg-black/70 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/80 backdrop-blur">
                      Sample Project
                    </span>
                  ) : (
                    <span className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full bg-ember px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white">
                      <span className="h-1.5 w-1.5 rounded-full bg-white" /> Live Website
                    </span>
                  )}
                </div>
                <div className="p-7">
                  <div className="flex items-center justify-between">
                    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ember">
                      {p.industry} · {p.location}
                    </p>
                    <ArrowUpRight className="h-5 w-5 text-white/20 transition-all group-hover:text-ember" />
                  </div>
                  <h2 className="mt-3 font-display text-2xl font-bold tracking-tight">{p.name}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-white/50">{p.objective}</p>
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    {p.services.map((s) => (
                      <span key={s} className="rounded-full border border-white/10 px-3 py-1 text-[11px] text-white/50">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            </Item>
          ))}
        </Stagger>

        <Reveal delay={0.1}>
          <div className="mt-16 rounded-3xl bg-ember p-8 text-center sm:p-12">
            <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
              Want results like these for your business?
            </h2>
            <p className="mx-auto mt-3 max-w-md text-sm text-white/80">
              Tell me what you do and who you want to reach — you'll get a strategy built around enquiries.
            </p>
            <button
              data-testid="projects-cta-btn"
              onClick={openLeadPopup}
              className="mt-7 rounded-full bg-white px-7 py-3.5 font-display text-xs font-bold uppercase tracking-wider text-obsidian transition-transform hover:scale-105"
            >
              Get a Free Website Plan
            </button>
            <Link
              to="/reviews/"
              data-testid="projects-reviews-link"
              className="mt-4 inline-block text-xs font-semibold text-obsidian/60 underline-offset-2 hover:underline"
            >
              See what clients say about working with us
            </Link>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
